import { orderBy } from "lodash";

import { chosenSites } from "app/common/services/chosenSites";
import type { Axis } from "app/components/RankingAxis/types";

import type { SearchTrafficDataSchema } from "../services/search-overview-api/types";

type DomainInfo = { domain: string; favicon: string };
type TrafficDataTransformer = (data: SearchTrafficDataSchema, domains: DomainInfo[]) => Axis[];

function buildAxisData(
    domains: DomainInfo[],
    getAbsoluteValue: (domain: string) => number,
    getRankChange?: (domain: string) => number,
): Axis[] {
    const items = domains.map(({ domain, favicon }) => {
        const absoluteValue = getAbsoluteValue(domain);
        const rankChange = getRankChange ? getRankChange(domain) : 0;

        return {
            name: domain,
            image: favicon,
            absoluteValue,
            isSegment: false,
            isPrimaryEntity: false,
            rank: 0, // will be assigned after sorting
            rankChange,
        };
    });

    const sortedItems = orderBy(items, ["absoluteValue"], ["desc"]);

    sortedItems.forEach((item, index) => {
        item.rank = index + 1;
    });

    return sortedItems;
}

export const getDomains = (): DomainInfo[] => {
    const chosenDomains = chosenSites.sitelistForLegend();
    return chosenDomains.map((site: { name: string; icon: string }) => ({
        domain: site.name,
        favicon: site.icon,
    }));
};

export const transformPaidVisits: TrafficDataTransformer = (
    data: SearchTrafficDataSchema,
    domains: DomainInfo[],
): Axis[] => {
    return buildAxisData(
        domains,
        (domain) => data?.TrafficSourcesVisits?.[domain]?.Paid?.Average ?? 0,
        (domain) => data?.TrafficSourcesVisits?.[domain]?.Paid?.YoyChange ?? 0,
    );
};

export const transformPpcSpend: TrafficDataTransformer = (
    data: SearchTrafficDataSchema,
    domains: DomainInfo[],
): Axis[] => {
    return buildAxisData(
        domains,
        (domain) => data?.AdSpend?.[domain]?.PayPerClick?.Average ?? 0,
        (domain) => data?.AdSpend?.[domain]?.PayPerClick?.YoyChange ?? 0,
    );
};

export const transformCostPerVisit: TrafficDataTransformer = (
    data: SearchTrafficDataSchema,
    domains: DomainInfo[],
): Axis[] => {
    return buildAxisData(
        domains,
        (domain) => data?.AdSpend?.[domain]?.CostPerVisit?.CostPerVisit ?? 0,
        (domain) => data?.AdSpend?.[domain]?.CostPerVisit?.YoyChange ?? 0,
    );
};
